import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

const faqs = [
    {
        question: 'How does Michela see my clothes?',
        answer: 'You upload photos of your clothes to build your digital wardrobe. Michela analyzes each piece to understand its color, style, formality, and warmth level.',
    },
    {
        question: 'Is my data private?',
        answer: 'Yes. Your wardrobe photos and personal data are private by default. We don\'t sell your data or share it with third parties.',
    },
    {
        question: 'What if I don\'t like the outfit suggestions?',
        answer: 'Michela learns from your reactions. The more you use it, the better it understands your preferences and refines future suggestions.',
    },
    {
        question: 'How many clothes do I need to get started?',
        answer: 'You can start with as few as 10-15 items. The more pieces you add, the more outfit combinations Michela can create for you.',
    },
    {
        question: 'Does it work for all occasions?',
        answer: 'Yes. Whether it\'s a casual brunch, work meeting, date night, or formal event — just describe where you\'re going and Michela will suggest appropriate outfits.',
    },
    {
        question: 'What platforms is Michela available on?',
        answer: 'iOS and web apps are coming first, with Android following soon after.',
    },
    {
        question: 'How does virtual try-on work?',
        answer: 'Upload a photo of yourself and Michela shows how the outfit looks on you using AI.',
    },
    {
        question: 'Does it work for all styles and genders?',
        answer: 'Yes — Michela works with any wardrobe regardless of style or gender.',
    },
    {
        question: 'Can I save outfits for later?',
        answer: 'Yes, save any outfit to your Lookbook and access it anytime.',
    },
    {
        question: 'How long does it take to set up?',
        answer: 'Most people upload their wardrobe in 5-10 minutes. You can always add more later.',
    },
]

function FAQItem({ question, answer, isOpen, onToggle }: {
    question: string
    answer: string
    isOpen: boolean
    onToggle: () => void
}) {
    return (
        <div className="border-b border-paper-tertiary last:border-b-0">
            <button
                onClick={onToggle}
                className="w-full py-5 flex items-center justify-between text-left group"
            >
                <span className="font-medium text-ink pr-4">{question}</span>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                >
                    <ChevronDown className="w-5 h-5 text-ink-tertiary group-hover:text-ink transition-colors" />
                </motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <p className="pb-5 text-ink-tertiary leading-relaxed pr-8">
                            {answer}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export function FAQ() {
    const [openIndex, setOpenIndex] = useState<number | null>(0)

    return (
        <section className="py-20 sm:py-28 bg-paper-alt">
            <div className="max-w-2xl mx-auto px-8 sm:px-6">
                <div className="text-center mb-12 sm:mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="font-display text-3xl sm:text-4xl font-semibold text-ink mb-4"
                    >
                        Common questions
                    </motion.h2>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-paper rounded-2xl border border-paper-tertiary p-6 sm:p-8"
                >
                    {faqs.map((faq, index) => (
                        <FAQItem
                            key={faq.question}
                            question={faq.question}
                            answer={faq.answer}
                            isOpen={openIndex === index}
                            onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                        />
                    ))}
                </motion.div>
            </div>
        </section>
    )
}

export default FAQ
